import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Button from 'components/generics/Button/Button';
import ConfirmDelete from 'components/generics/ConfirmDelete/ConfirmDelete';
import { Loader } from 'components/generics/Loader/Loader';
import DeleteIcon from 'components/icons/DeleteIcon';

import { download, downloadZip, list, remove, upload } from 'services/API/Attachment';

import './Attachments.style.scss';

const isImage = attachment => (attachment.mimeType || '').startsWith('image/');

const formatSize = bytes => {
    if (!bytes) {
        return '0 o';
    }
    const units = ['o', 'Ko', 'Mo', 'Go'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${index === 0 ? value : value.toFixed(1)} ${units[index]}`;
};

const errorMessage = err => {
    const data = err?.response?.data;
    if (typeof data === 'string') {
        return data;
    }
    if (data && typeof data.message === 'string') {
        return data.message;
    }
    if (data && typeof data.error === 'string') {
        return data.error;
    }
    return err?.message || 'Unexpected error';
};

const saveBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
};

/** Vignette d'aperçu : l'image est récupérée via l'API authentifiée (blob). */
const AttachmentThumb = ({ flightId, attachment }) => {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        let active = true;
        let objectUrl = null;

        download(flightId, attachment.id)
            .then(resp => {
                if (!active) {
                    return;
                }
                objectUrl = window.URL.createObjectURL(
                    new Blob([resp.data], { type: attachment.mimeType })
                );
                setUrl(objectUrl);
            })
            .catch(() => {
                // Pas d'aperçu possible : la ligne reste utilisable.
            });

        return () => {
            active = false;
            if (objectUrl) {
                window.URL.revokeObjectURL(objectUrl);
            }
        };
    }, [flightId, attachment.id, attachment.mimeType]);

    if (!url) {
        return <span className="attachments__thumb attachments__thumb--placeholder" />;
    }
    return <img className="attachments__thumb" src={url} alt={attachment.originalName} />;
};

const AttachmentsSection = ({ flightId }) => {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const fileInputRef = useRef(null);

    const load = () => list(flightId).then(resp => setAttachments(resp.data || []));

    useEffect(() => {
        if (!flightId) {
            return undefined;
        }
        let active = true;
        setLoading(true);
        list(flightId)
            .then(resp => {
                if (active) {
                    setAttachments(resp.data || []);
                }
            })
            .catch(err => {
                if (active) {
                    toast.error(errorMessage(err));
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [flightId]);

    const onFilesSelected = event => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        setUploading(true);
        upload(flightId, files)
            .then(() => {
                toast.success('Attachment(s) added');
                return load();
            })
            .catch(err => toast.error(errorMessage(err)))
            .finally(() => {
                setUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            });
    };

    const onDownload = attachment => {
        download(flightId, attachment.id)
            .then(resp => saveBlob(resp.data, attachment.originalName))
            .catch(err => toast.error(errorMessage(err)));
    };

    const onDownloadAll = () => {
        downloadZip(flightId)
            .then(resp => saveBlob(resp.data, `flight_${flightId}.zip`))
            .catch(err => toast.error(errorMessage(err)));
    };

    const onConfirmDelete = () => {
        const attachment = confirmDelete;
        setConfirmDelete(null);
        setDeleting(true);
        remove(flightId, attachment.id)
            .then(() => {
                toast.success('Attachment deleted');
                return load();
            })
            .catch(err => toast.error(errorMessage(err)))
            .finally(() => setDeleting(false));
    };

    return (
        <div className="attachments">
            <div className="attachments__header">
                <h3>ATTACHMENTS</h3>
                <div className="attachments__actions">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={onFilesSelected}
                    />
                    <Button
                        text={uploading ? 'UPLOADING...' : 'ADD ATTACHMENT(S)'}
                        defaultStyle="primary"
                        disabled={uploading}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    />
                    <Button
                        text="DOWNLOAD ALL"
                        defaultStyle="primary"
                        disabled={loading || deleting || attachments.length === 0}
                        onClick={onDownloadAll}
                    />
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : attachments.length === 0 ? (
                <p className="attachments__empty">No attachment for this flight.</p>
            ) : (
                <ul className="attachments__list">
                    {attachments.map(attachment => (
                        <li key={attachment.id} className="attachments__item">
                            {isImage(attachment) ? (
                                <AttachmentThumb flightId={flightId} attachment={attachment} />
                            ) : (
                                <span className="attachments__thumb attachments__thumb--file">
                                    {(attachment.originalName.split('.').pop() || 'FILE')
                                        .toUpperCase()
                                        .slice(0, 4)}
                                </span>
                            )}
                            <span
                                className="attachments__name"
                                title={`Download ${attachment.originalName}`}
                                onClick={() => onDownload(attachment)}>
                                {attachment.originalName}
                            </span>
                            <span className="attachments__size">{formatSize(attachment.size)}</span>
                            <span
                                className="attachments__delete"
                                title="Delete attachment"
                                onClick={() => setConfirmDelete(attachment)}>
                                <DeleteIcon width={16} height={20} color="#e74c3c" fill="#e74c3c" />
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {confirmDelete && (
                <ConfirmDelete
                    onClose={() => setConfirmDelete(null)}
                    list={[confirmDelete]}
                    onConfirm={onConfirmDelete}
                    formatList={item => item.originalName}
                />
            )}
        </div>
    );
};

export default AttachmentsSection;
